import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, Linkedin, ArrowRight, Loader2 } from "lucide-react";

const initialForm = { name: "", organisation: "", email: "", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const validate = (data) => {
    const next = {};
    if (!data.name.trim()) next.name = "Please share your name";
    if (!data.email.trim()) next.email = "An email address is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email.trim()))
      next.email = "That email does not look right";
    if (!data.message.trim() || data.message.trim().length < 5)
      next.message = "A short message helps me prepare";
    return next;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    if (errors[name]) setErrors((er) => ({ ...er, [name]: undefined }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const v = validate(form);
    setErrors(v);
    if (Object.keys(v).length) return;

    setSubmitting(true);
    try {
      const formElement = e.currentTarget;
      const formData = new FormData(formElement);
      const encoded = new URLSearchParams(formData).toString();

      const response = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encoded,
      });

      if (response.ok) {
        setSubmitted(true);
        setForm(initialForm);
        toast.success("Enquiry sent. Elena will be in touch shortly.");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch (err) {
      toast.error("Could not send your message just now.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-16 md:py-24 bg-[#9CDBD9]/15"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20">
        {/* Left — invitation */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-5"
        >
          <p className="font-sans-body text-xs tracking-widest-2 uppercase text-[#3E7373] mb-6 flex items-center gap-3">
            <span className="inline-block w-8 h-px bg-[#3E7373]" />
            Get in touch
          </p>
          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-[#0D0D0D] font-medium">
            Let us start
            <br />
            <span className="italic text-[#3E7373]">a conversation.</span>
          </h2>
          <p className="font-sans-body text-base md:text-lg text-[#4A4A4A] mt-8 leading-relaxed max-w-md">
            Whether you have a defined brief or a question you have not yet been able to articulate, I would be glad to hear from you. All enquiries are answered personally.
          </p>

          <div className="mt-12 space-y-5 font-sans-body">
            <a
              href="mailto:elena@theopriskcompany.com"
              data-testid="contact-email-link"
              className="group flex items-center gap-4 text-[#0D0D0D] hover:text-[#3E7373]"
            >
              <span className="w-10 h-10 border border-[#0D0D0D]/20 group-hover:border-[#3E7373] flex items-center justify-center">
                <Mail size={16} strokeWidth={1.4} />
              </span>
              <span className="text-base">elena@theopriskcompany.com</span>
            </a>
            <a
              href="https://www.linkedin.com/in/elenapykhova/"
              target="_blank"
              rel="noreferrer noopener"
              data-testid="contact-linkedin-link"
              className="group flex items-center gap-4 text-[#0D0D0D] hover:text-[#3E7373]"
            >
              <span className="w-10 h-10 border border-[#0D0D0D]/20 group-hover:border-[#3E7373] flex items-center justify-center">
                <Linkedin size={16} strokeWidth={1.4} />
              </span>
              <span className="text-base">Connect on LinkedIn</span>
            </a>
          </div>
        </motion.div>

        {/* Right — form */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-7"
        >
          {submitted ? (
            <div
              data-testid="contact-success-message"
              className="bg-white border border-[#9CDBD9] p-10 md:p-14"
            >
              <p className="font-sans-body text-xs tracking-widest-2 uppercase text-[#3E7373] mb-5">
                Thank you
              </p>
              <h3 className="font-serif-display text-3xl md:text-4xl text-[#0D0D0D] leading-snug">
                Your message is with me.
              </h3>
              <p className="font-sans-body text-[#4A4A4A] mt-5 leading-relaxed max-w-md">
                I read every enquiry personally and will respond within two working days. In the meantime, do feel free to add me on LinkedIn.
              </p>
              <button
                type="button"
                onClick={() => setSubmitted(false)}
                data-testid="contact-send-another"
                className="mt-8 inline-flex items-center gap-2 font-sans-body text-sm tracking-widest-2 uppercase text-[#0D0D0D] hover:text-[#3E7373]"
              >
                Send another message <ArrowRight size={16} />
              </button>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              noValidate
              data-netlify="true"
              name="contact"
              method="POST"
              action="/"
              data-testid="contact-form"
              className="bg-white border border-[#E5E5E5] p-8 md:p-12 space-y-8"
            >
              <input type="hidden" name="form-name" value="contact" />
              <Field
                label="Your name"
                name="name"
                value={form.name}
                onChange={handleChange}
                error={errors.name}
                placeholder="First and last name"
                testId="contact-input-name"
              />
              <Field
                label="Organisation"
                name="organisation"
                value={form.organisation}
                onChange={handleChange}
                placeholder="Optional"
                testId="contact-input-organisation"
              />
              <Field
                label="Email"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                error={errors.email}
                placeholder="you@company.com"
                testId="contact-input-email"
              />
              <TextareaField
                label="What is on your mind?"
                name="message"
                value={form.message}
                onChange={handleChange}
                error={errors.message}
                placeholder="A few lines about your context and what you are hoping to achieve."
                testId="contact-input-message"
              />

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 pt-2">
                <p className="font-sans-body text-xs text-[#4A4A4A] max-w-xs leading-relaxed">
                  I treat every enquiry in confidence. Your details will not be shared.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-button"
                  className="inline-flex items-center justify-center gap-3 bg-[#0D0D0D] text-white px-8 py-4 font-sans-body text-sm tracking-widest-2 uppercase hover:bg-[#3E7373] disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Sending
                    </>
                  ) : (
                    <>
                      Send enquiry <ArrowRight size={16} />
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};

const Field = ({ label, name, value, onChange, error, placeholder, type = "text", testId }) => (
  <div>
    <label
      htmlFor={name}
      className="block font-sans-body text-[11px] tracking-widest-2 uppercase text-[#3E7373] mb-3"
    >
      {label}
    </label>
    <input
      id={name}
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      data-testid={testId}
      className={`w-full bg-transparent border-0 border-b ${
        error ? "border-red-500" : "border-[#0D0D0D]/25"
      } focus:border-[#3E7373] focus:outline-none py-3 font-sans-body text-[#0D0D0D] placeholder:text-[#0D0D0D]/30 transition-colors duration-300`}
    />
    {error && (
      <p data-testid={`${testId}-error`} className="mt-2 text-xs text-red-600 font-sans-body">
        {error}
      </p>
    )}
  </div>
);

const TextareaField = ({ label, name, value, onChange, error, placeholder, testId }) => (
  <div>
    <label
      htmlFor={name}
      className="block font-sans-body text-[11px] tracking-widest-2 uppercase text-[#3E7373] mb-3"
    >
      {label}
    </label>
    <textarea
      id={name}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={5}
      data-testid={testId}
      className={`w-full bg-transparent border-0 border-b ${
        error ? "border-red-500" : "border-[#0D0D0D]/25"
      } focus:border-[#3E7373] focus:outline-none py-3 font-sans-body text-[#0D0D0D] placeholder:text-[#0D0D0D]/30 resize-none transition-colors duration-300`}
    />
    {error && (
      <p data-testid={`${testId}-error`} className="mt-2 text-xs text-red-600 font-sans-body">
        {error}
      </p>
    )}
  </div>
);

export default Contact;
